/**
 * IntervalReadout — renders any `{ value, margin, n, scope? }` parts object, the shape both
 * `Metric.toParts()` (`src/lib/runs/metrics.ts`) and `EvidenceEnvelope.toParts()`
 * (`src/lib/growth/evidence/envelope.ts`) already produce. Layout only — no new formatting logic
 * (`design-spec.md:221`): `value` and `margin` are rendered as-is (they already carry `NO_DATA`
 * and parenthesised-range handling from their source), `n` is delegated entirely to
 * `SampleSizeLabel` rather than calling `int()` a second time, and `scope` (when present) is
 * rendered as-is too — it already arrives formatted via `metrics.ts`'s `formatScope()`
 * (`growth-engine-r2b-population` B1.3), the one place a `Window` becomes a rendered label.
 *
 * `n <= 0` hides the sample-size label rather than showing a fabricated "0 …" — this covers both
 * the ordinary no-data case (PRD-03) and `EvidenceEnvelope`'s `hypothesis` classification, whose
 * `n` is definitionally 0 or near-0 always (`envelope.ts`'s "Why `hypothesis` is exempt" note) —
 * neither should render a sample-size chip that implies a real count was taken.
 *
 * `scope` is OPTIONAL, not required, even though `Metric.toParts()` always sets it. This is a
 * deliberate B1.3 resolution, not an oversight: `EvidenceEnvelope.toParts()` produces the same
 * `{ value, margin, n }` shape and `IntervalReadout` has always accepted both interchangeably
 * (BRIEF.md's "Decided" section — `IntervalReadout` "already accepts both because they are
 * structurally compatible"). `EvidenceEnvelope` carries no population/window concept at all —
 * adding one would mean plumbing `Population`/`Window` through every `fromObserved`/`fromEstimated`/
 * `fromInferred`/`asHypothesis` call site across the growth codebase, well outside this task's
 * `files:` line (`metrics.ts`, `interval-readout.tsx` only). Making `scope` optional here keeps
 * `EvidenceEnvelope` compiling unchanged and lets this component render nothing extra for a parts
 * object that doesn't carry one, rather than forcing every envelope call site to invent a value
 * it doesn't have.
 *
 * R6.3 I1 — the one interval form. A `Metric.toParts()` result carries `range`, and for it this
 * renders the value on its own row and `IntervalLine` under it: "14% to 55% · 10 questions · 28 days",
 * the maturity cue ("Early data · 2 prompts") only below the threshold. Parts without `range` keep
 * the layout described above.
 *
 * WHEN: anywhere a `Metric` or `EvidenceEnvelope` needs to reach the screen — pass its
 *   `.toParts()` output straight through.
 * NEVER: reformat `parts.value` / `parts.margin` / `parts.scope`, or call `int()` on `parts.n` at
 *   the call site — both duplicate formatting this component and `SampleSizeLabel` already own
 *   (DES-04).
 */
import { Fragment } from 'react'
import { SampleSizeLabel } from '@/components/shared/sample-size-label'
import { intervalSegments, type IntervalSegmentsInput } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * Structurally compatible with `Metric.toParts()`'s return type and with `EnvelopeParts` (which
 * carries additional fields `IntervalReadout` does not need) — a caller passes either straight
 * through with no adapter. `scope` is optional — see the file docblock for why.
 *
 * `range` (R6.3 I1) is present on every `Metric.toParts()` result. Its presence selects the one
 * interval form; parts without it (`EvidenceEnvelope`, the Focus pp-change intervals, a share
 * snapshot frozen before the field existed) keep the inline bracketed margin. One parts object
 * renders one form — never both.
 */
export interface IntervalReadoutParts {
  readonly value: string
  readonly margin: string
  readonly range?: string
  readonly n: number
  readonly scope?: string
}

interface IntervalLineProps extends IntervalSegmentsInput {
  className?: string
}

/**
 * IntervalLine — the support line of the one interval form (R6.3 I1): `intervalSegments()` mapped
 * to spans and joined by " · ", the range first and set slightly stronger, as the approved frame
 * draws it ("14% to 55% · 10 questions · ~1 check each"). The email and the PDF render the same
 * segments through `intervalLineText()`, so the three read identically.
 *
 * Renders nothing with no sample (`n <= 0`, PRD-03) — the value beside it is then the no-data
 * glyph and there is no line, never "0 prompts".
 *
 * WHEN: under any visibility rate, on its support line — `IntervalReadout` already renders it for a
 *   `Metric.toParts()` result; use it directly only where the value sits in a frame of its own
 *   (the hero's band-1 readout, the "How we measure" sheet).
 * NEVER: build the line by hand, add brackets, or put a `SampleSizeLabel` beside it.
 */
export function IntervalLine({
  className,
  ...input
}: IntervalLineProps): React.ReactElement | null {
  const segments = intervalSegments(input)
  if (segments.length === 0) return null
  const hasRange = Boolean(input.range)
  return (
    <div
      data-slot="interval-line"
      className={cn('text-caption tabular-nums text-muted-foreground', className)}
    >
      {segments.map((segment, i) => (
        <Fragment key={i}>
          {i > 0 && <span> · </span>}
          {i === 0 && hasRange ? (
            <span className="font-medium text-foreground">{segment}</span>
          ) : (
            segment
          )}
        </Fragment>
      ))}
    </div>
  )
}

export interface IntervalReadoutProps {
  parts: IntervalReadoutParts
  /** Unit word for the sample — passed to `IntervalLine` (or `SampleSizeLabel` on legacy parts). */
  unit?: string
  className?: string
}

export function IntervalReadout({
  parts,
  unit,
  className,
}: IntervalReadoutProps): React.ReactElement {
  const { value, margin, range, n, scope } = parts
  if (range !== undefined) {
    // The one form: the value on its own row, the range + sample + scope on the support line.
    return (
      <div className={cn('flex flex-col gap-0.5', className)}>
        <span className="text-body font-medium tabular-nums text-foreground">{value}</span>
        <IntervalLine range={range} n={n} unit={unit ?? 'scan'} scope={scope} />
      </div>
    )
  }
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <span className="text-body font-medium tabular-nums text-foreground">
        {value}
        {margin ? <span className="ml-1 font-normal text-muted-foreground">{margin}</span> : null}
      </span>
      {n > 0 && <SampleSizeLabel n={n} unit={unit} />}
      {scope && <span className="text-caption text-muted-foreground">{scope}</span>}
    </div>
  )
}
